import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight } from '@fortawesome/pro-light-svg-icons/faChevronRight';
import type { OnboardingAction, OnboardingChecklistItem } from 'fym-shared/src/music24/onboardingChecklist';
import { TypedMemo } from '../../common/types';
import { SMLButton } from '../../music24/components/primitives/SMLButton';
import { checklistItemMessages, checklistMessages as messages } from './checklistMessages';
import { withBrandWord } from '../../music24/components/primitives/BrandWord';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the checklist; each part uses its own slice of it */
import styles from './FirstReleaseChecklist.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ChecklistNextStepProps {
  item: OnboardingChecklistItem;
  readyAssets: number;
  /** Days left of the trial, or null when the user is not on the trial. */
  trialDaysLeft: number | null;
  onAction: (action: OnboardingAction) => void;
}

/**
 * The left column of the card: ONE next step, with what it gives (design: onboarding d1, d3). The
 * eyebrow says "Next step" with no number: the card head has the one progress count.
 */
const ChecklistNextStepComponent: React.FC<ChecklistNextStepProps> = (props) => {
  const { item, readyAssets, trialDaysLeft, onAction } = props;
  const intl = useIntl();
  const copy = checklistItemMessages[item.key];
  const runAction = useCallback(() => onAction(item.action), [item.action, onAction]);
  const showTrialLine = item.key === 'planPick' && trialDaysLeft !== null;

  return (
    <div className={styles.next} data-testid="checklist-next">
      <span className={styles.eyebrow}>{intl.formatMessage(messages.next)}</span>
      <b className={styles.nextTitle}>{intl.formatMessage(copy.title)}</b>
      <p>{withBrandWord(intl.formatMessage(copy.body, { count: readyAssets }))}</p>
      <div className={styles.nextActions}>
        <SMLButton variant="primary" onClick={runAction}>
          {intl.formatMessage(copy.action)}
          <FontAwesomeIcon icon={faChevronRight} />
        </SMLButton>
        {showTrialLine && (
          <span className={styles.tiny}>{intl.formatMessage(messages.trialLine, { days: trialDaysLeft })}</span>
        )}
      </div>
    </div>
  );
};

export const ChecklistNextStep = TypedMemo(ChecklistNextStepComponent);
